import React from 'react';
import { Handle, Position } from 'reactflow';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Brain, Mail, Database, Globe, Sparkles, MessageSquare,
  FileText, ThumbsUp, FileSearch, Timer, Code, ShieldCheck,
  Variable, Braces, Bot,
} from 'lucide-react';

const NODE_ICON_MAP = {
  OpenAI: Brain,
  AITransform: Sparkles,
  TextSummarizer: FileText,
  SentimentAnalysis: ThumbsUp,
  SendEmail: Mail,
  SlackNotification: MessageSquare,
  PostgreSQLQuery: Database,
  MySQLQuery: Database,
  HttpRequest: Globe,
  WebScraper: FileSearch,
  SetVariable: Variable,
  JSONTransform: Braces,
  DelayTimer: Timer,
  CodeExecutor: Code,
  DataValidator: ShieldCheck,
  MarketplaceAgent: Bot,
};

const NODE_COLORS = {
  AI: { bg: 'rgba(248, 114, 23, 0.12)', border: '#F87217', icon: '#F87217' },
  Communication: { bg: 'rgba(245,158,11,0.12)', border: '#F59E0B', icon: '#F59E0B' },
  Data: { bg: 'rgba(59,130,246,0.12)', border: '#3B82F6', icon: '#3B82F6' },
  HTTP: { bg: 'rgba(6,182,212,0.12)', border: '#06B6D4', icon: '#06B6D4' },
  Logic: { bg: 'rgba(239,68,68,0.12)', border: '#EF4444', icon: '#EF4444' },
  Utilities: { bg: 'rgba(100,116,139,0.12)', border: '#64748B', icon: '#94A3B8' },
};

const getCategoryFromType = (type) => {
  const aiTypes = ['OpenAI', 'MarketplaceAgent', 'AITransform', 'TextSummarizer', 'SentimentAnalysis'];
  const commTypes = ['SendEmail', 'SlackNotification'];
  const dataTypes = ['PostgreSQLQuery', 'MySQLQuery'];
  const httpTypes = ['HttpRequest', 'WebScraper'];
  const logicTypes = ['SetVariable', 'JSONTransform'];
  const utilTypes = ['DelayTimer', 'CodeExecutor', 'DataValidator'];
  if (aiTypes.includes(type)) return 'AI';
  if (commTypes.includes(type)) return 'Communication';
  if (dataTypes.includes(type)) return 'Data';
  if (httpTypes.includes(type)) return 'HTTP';
  if (logicTypes.includes(type)) return 'Logic';
  if (utilTypes.includes(type)) return 'Utilities';
  return 'Utilities';
};

const STATUS_COLORS = {
  success: '#10B981',
  failed: '#EF4444',
  running: '#F59E0B',
  skipped: '#64748B',
};

export default function ActionNode({ data, selected }) {
  const { isDark } = useTheme();
  const Icon = NODE_ICON_MAP[data.type] || Bot;
  const category = getCategoryFromType(data.type);
  const colors = NODE_COLORS[category] || NODE_COLORS.Utilities;
  const statusColor = data.executionStatus ? STATUS_COLORS[data.executionStatus] : null;

  const bg = isDark ? '#0d1117' : '#ffffff';
  const handleBg = isDark ? '#0d1117' : '#ffffff';
  const text1 = isDark ? '#e6edf3' : '#0f172a';
  const boxShadow = selected
    ? `0 0 0 3px rgba(248,114,23,0.25), 0 4px 16px ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.1)'}`
    : `0 2px 8px ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.06)'}`;

  // For MarketplaceAgent nodes, show the selected agent name
  const isMarketplaceAgent = data.type === 'MarketplaceAgent';
  const agentName = isMarketplaceAgent && data.config?.agent_name
    ? data.config.agent_name
    : null;
  const agentId = isMarketplaceAgent && data.config?.agent_id
    ? data.config.agent_id
    : null;

  return (
    <div
      style={{
        background: bg,
        border: `2px solid ${selected ? '#F87217' : statusColor || colors.border}`,
        borderRadius: 12,
        padding: '10px 14px',
        minWidth: 170,
        boxShadow: boxShadow,
        transition: 'all 0.18s ease',
        cursor: 'default',
        position: 'relative',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          width: 10,
          height: 10,
          background: colors.border,
          border: `2px solid ${handleBg}`,
          left: -6,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: colors.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: `1px solid ${colors.border}30`,
          }}
        >
          <Icon size={14} color={colors.icon} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: colors.icon,
              marginBottom: 1,
            }}
          >
            {category}
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: text1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label || data.type}
          </div>
          {/* Agent badge for MarketplaceAgent nodes */}
          {isMarketplaceAgent && (agentName || agentId) && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                marginTop: 4,
                padding: '2px 7px',
                background: 'rgba(6, 182, 212, 0.12)',
                border: '1px solid rgba(6, 182, 212, 0.35)',
                borderRadius: 100,
                maxWidth: '100%',
              }}
            >
              <Bot size={9} color="#06B6D4" />
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#06B6D4',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 110,
                  letterSpacing: '0.02em',
                }}
              >
                {agentName || agentId.slice(0, 8) + '…'}
              </span>
            </div>
          )}
          {/* Placeholder badge when no agent selected */}
          {isMarketplaceAgent && !agentName && !agentId && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                marginTop: 4,
                padding: '2px 7px',
                background: 'rgba(100, 116, 139, 0.1)',
                border: '1px dashed rgba(100, 116, 139, 0.4)',
                borderRadius: 100,
              }}
            >
              <span style={{ fontSize: 9, color: '#64748B', fontStyle: 'italic' }}>
                No agent selected
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Execution status indicator */}
      {data.executionStatus && (
        <div
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: statusColor,
            border: `2px solid ${bg}`,
            boxShadow: `0 0 6px ${statusColor}80`,
          }}
        />
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: 10,
          height: 10,
          background: colors.border,
          border: `2px solid ${handleBg}`,
          right: -6,
        }}
      />
    </div>
  );
}
